import React, { useMemo } from "react";
import classNames from "classnames";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Shield } from "lucide-react";
import { useTranslation } from "react-i18next";

import Modal from "../../components/Modal/Modal";
import styles from "./RiskControlCenterRiskModal.module.scss";
import Button from "../../components/Button/Button";
import { formatDate } from "../../utils/helpers";
import { hideModal } from "../../redux/modalStore";

const normalizeLevel = (level) => {
  const normalized = String(level || "none").trim().toLowerCase();
  if (["high", "medium", "low", "none"].includes(normalized)) return normalized;
  return "none";
};

const RiskControlCenterRiskModal = ({ risksData }) => {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const openedModal = useSelector((state) => state.modal.openedModal);

  const riskNum =
    openedModal?.name === "risk_control_center_risk_modal"
      ? openedModal?.data?.riskNum
      : null;

  const risk = useMemo(() => {
    if (riskNum === null || riskNum === undefined) return null;
    return risksData?.[riskNum] || null;
  }, [riskNum, risksData]);

  const modalTitle = risk?.risk_id
    ? `${risk.risk_id} — ${risk?.description || ""}`.trim()
    : t("riskControlCenter.riskModal.title");

  return (
    <Modal
      name="risk_control_center_risk_modal"
      title={modalTitle}
      subtitle={t("riskControlCenter.riskModal.subtitle")}
      width="720px"
      maxWidth="calc(100vw - 40px)"
    >
      {!risk ? (
        <div className={styles.emptyState}>
          {t("riskControlCenter.riskModal.notFound")}
        </div>
      ) : (
        <div className={styles.wrapper}>
          <div className={styles.summaryRow}>
            <div className={styles.summaryLeft}>
              {risk.risk_id && (
                <span className={styles.riskPill}>{risk.risk_id}</span>
              )}
              <span
                className={classNames(
                  styles.levelPill,
                  styles[`pill_${normalizeLevel(risk.level)}`],
                )}
              >
                {t(`tableAccordion.level.${normalizeLevel(risk.level)}`)}
              </span>
            </div>
            {risk.date && (
              <div className={styles.summaryRight}>
                <span className={styles.updatedText}>
                  {t("riskControlCenter.riskModal.fields.updated")}:{" "}
                  {formatDate(risk.date)}
                </span>
              </div>
            )}
          </div>

          <div className={styles.grid}>
            <div className={styles.fieldGroup}>
              <div className={styles.label}>
                {t("riskControlCenter.riskModal.fields.qms")}
              </div>
              <div className={styles.valueBox}>
                {risk.qms_id
                  ? `${risk.qms_id} ${risk.qms_description || ""}`.trim()
                  : "-"}
              </div>
            </div>

            <div className={styles.fieldGroup}>
              <div className={styles.label}>
                {t("riskControlCenter.riskModal.fields.level")}
              </div>
              <div className={styles.valueBox}>
                {t(`tableAccordion.level.${normalizeLevel(risk.level)}`)}
              </div>
            </div>
          </div>

          <div className={styles.section}>
            <div className={styles.label}>
              {t("riskControlCenter.riskModal.fields.reason")}
            </div>
            <div className={classNames(styles.valueBox, styles.valueBoxMultiline)}>
              {risk.reason || t("riskProfile.noReason")}
            </div>
          </div>

          <div className={styles.actions}>
            <Button
              type="button"
              className={styles.cancelButton}
              text={t("cancel")}
              onClick={() => dispatch(hideModal())}
            />
            <Button
              type="button"
              className={styles.openButton}
              text={t("riskControlCenter.riskModal.openRiskProfile")}
              icon={Shield}
              onClick={() => {
                dispatch(hideModal());
                navigate(`/${orgId}/risk-profile`);
              }}
            />
          </div>
        </div>
      )}
    </Modal>
  );
};

export default RiskControlCenterRiskModal;
